/**
 * BhiBtrePage — iter982 · Sprint 3
 *
 * BTRE (Breast Tissue Restoration Engine) — turns the top-N pathways from
 * Sprint 2 into a ranked list of concrete intervention priorities.
 *
 * Route: /dashboard/bhi/btre
 * Backend: GET /api/bhi/btre/priorities?top_n=5&module=btre&k=2
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Target, Activity, ArrowRight, CheckCircle2,
  Sparkles, RefreshCw, Award, TrendingDown,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import WeeklyPlanPanel from "@/components/breast_intel/WeeklyPlanPanel";

const SCORE_TONE = (v) => {
  if (v >= 0.75) return "bg-rose-500";
  if (v >= 0.5)  return "bg-amber-500";
  if (v >= 0.25) return "bg-sky-500";
  return "bg-emerald-500";
};

const CATEGORY_ACCENT = {
  lifestyle:                   "text-rose-500 bg-rose-500/10 border-rose-500/30",
  anthropometric:              "text-sky-500 bg-sky-500/10 border-sky-500/30",
  reproductive:                "text-violet-500 bg-violet-500/10 border-violet-500/30",
  medical_history:             "text-amber-500 bg-amber-500/10 border-amber-500/30",
  medication:                  "text-indigo-500 bg-indigo-500/10 border-indigo-500/30",
  endocrine_disorder:          "text-emerald-500 bg-emerald-500/10 border-emerald-500/30",
  environmental:               "text-orange-500 bg-orange-500/10 border-orange-500/30",
  benign:                      "text-teal-500 bg-teal-500/10 border-teal-500/30",
  family_history:              "text-fuchsia-500 bg-fuchsia-500/10 border-fuchsia-500/30",
};

function accentFor(cat) {
  return CATEGORY_ACCENT[cat] || "text-slate-500 bg-slate-500/10 border-slate-500/30";
}

export default function BhiBtrePage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [topN, setTopN] = useState(5);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bhi/btre/priorities`, {
        params: { top_n: topN, module: "btre" },
      });
      setData(data);
    } catch {
      toast.error("Грешка при зареждане на BTRE приоритетите.");
    } finally {
      setLoading(false);
    }
  }, [API, topN]);

  useEffect(() => { load(); }, [load]);

  const themeBg = isDark ? "bg-slate-900" : "bg-slate-50";
  const cardCls = isDark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200";
  const mutedCls = isDark ? "text-slate-400" : "text-slate-500";
  const chipCls = isDark ? "bg-slate-700/60 text-slate-100" : "bg-slate-100 text-slate-700";

  if (loading) {
    return (
      <div className={`min-h-screen ${themeBg} flex items-center justify-center`} data-testid="btre-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  const hasAny = data && data.priorities && data.priorities.length > 0;
  const hasActionableLevers = hasAny && data.priorities.some((p) => p.drivers.some((d) => !d.already_optimal));

  const CATEGORY_LABELS_BG = {
    lifestyle: "Начин на живот",
    anthropometric: "Тегло и телесен състав",
    reproductive: "Репродуктивна история",
    medical_history: "Медицинска история",
    medication: "Медикаменти",
    endocrine_disorder: "Ендокринни състояния",
    environmental: "Околна среда",
    benign: "Доброкачествени лезии",
    family_history: "Фамилна анамнеза",
    pregnancy_related_endocrine_disorder: "Ендокринни в бременност",
  };

  return (
    <div className={`min-h-screen ${themeBg} pb-16`} data-testid="btre-page">
      {/* Header */}
      <div className={`${cardCls} border-b sticky top-0 z-10 backdrop-blur bg-opacity-90`}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/health-intelligence/breast" className="text-sm hover:underline flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> BHI
          </Link>
          <div className="flex-1">
            <h1 className="text-lg md:text-xl font-bold flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              BTRE Приоритети
            </h1>
            <p className={`text-xs ${mutedCls}`}>
              Твоите топ-{data?.top_n || topN} интервенционни цели, подредени по въздействие.
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="btre-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Обнови
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {!hasAny || !hasActionableLevers ? (
          <div className={`${cardCls} border rounded-xl p-6 text-center ${mutedCls}`}
               data-testid="btre-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div className="font-semibold mb-1 text-base">
              {hasAny ? "Няма актуални интервенционни цели" : "Няма данни за приоритети"}
            </div>
            <div className="text-sm">
              {hasAny
                ? "Всички твои фактори, върху които можеш да въздействаш, вече са на оптимално ниво или все още нямаш избрани такива."
                : "Избери поне 1 фактор, върху който можеш да въздействаш, за да генерираме твоя план за действие."}
              {" "}
              <Link to="/health-intelligence/breast" className="text-emerald-500 hover:underline">
                Отвори „Твоят път към превенция" →
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Overall relief card */}
            <div className={`${cardCls} border rounded-xl p-4`} data-testid="btre-overall">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-500/15 text-emerald-500 flex items-center justify-center shrink-0">
                  <TrendingDown className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base md:text-lg font-bold">
                      Общ потенциал за облекчение:
                      {" "}
                      <span className="text-emerald-500">{Math.round(data.overall_potential_relief_pct * 100)}%</span>
                    </h2>
                  </div>
                  <p className={`text-xs md:text-sm mt-1 ${mutedCls}`}>
                    Ако постигнеш всички долни цели, натоварването върху топ-{data.priorities.length} биологични процеси ще падне с
                    приблизително <b>{Math.round(data.overall_potential_relief_pct * 100)}%</b>.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${chipCls}`}>
                      Активни фактори: <b>{data.total_active_factors}</b>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-priority cards */}
            {data.priorities.map((p) => {
              const pct = Math.max(0, Math.min(100, Math.round(p.risk_score * 100)));
              const reliefPct = Math.round(p.total_potential_relief_pct * 100);
              return (
                <div key={p.pathway_id} className={`${cardCls} border rounded-xl p-4 space-y-3`}
                     data-testid={`btre-priority-${p.pathway_id}`}>
                  {/* Header row */}
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm ${
                      p.rank === 1 ? "bg-rose-500" :
                      p.rank === 2 ? "bg-amber-500" :
                      p.rank === 3 ? "bg-sky-500"   : "bg-slate-500"
                    }`}>
                      #{p.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm md:text-base">{p.pathway_label_bg}</h3>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        <span className={mutedCls}>Ниво на активност</span>
                        <span className="font-mono font-semibold">{p.risk_score.toFixed(2)}</span>
                        {p.protective_score > 0 && (
                          <span className="text-emerald-500">
                            · защита −{p.protective_score.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className={`mt-1.5 h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-700" : "bg-slate-200"}`}>
                        <div className={`h-full ${SCORE_TONE(p.risk_score)} transition-all`}
                             style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    {reliefPct > 0 && (
                      <div className="text-right shrink-0">
                        <div className="text-xs text-emerald-500 font-semibold flex items-center gap-1">
                          <TrendingDown className="w-3.5 h-3.5" />
                          −{reliefPct}%
                        </div>
                        <div className={`text-[10px] ${mutedCls}`}>възможно</div>
                      </div>
                    )}
                  </div>

                  {/* Drivers */}
                  {p.drivers.length === 0 ? (
                    <div className={`text-xs ${mutedCls} pl-12 flex items-center gap-1.5`}>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      Няма фактори, върху които да въздействаш и които да задвижват този процес.
                    </div>
                  ) : (
                    <div className="pl-2 md:pl-12 space-y-2" data-testid={`btre-drivers-${p.pathway_id}`}>
                      {p.drivers.map((d) => (
                        <div key={d.factor_id}
                             className={`rounded-lg border p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-4 ${
                               isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-slate-50/60"
                             }`}
                             data-testid={`btre-driver-${p.pathway_id}-${d.factor_id}`}>
                          {/* Current */}
                          <div className="min-w-0">
                            <div className={`text-[10px] uppercase tracking-wide ${mutedCls}`}>Сега</div>
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${accentFor(d.category)}`}>
                                {CATEGORY_LABELS_BG[d.category] || d.category?.replace(/_/g, " ")}
                              </span>
                              <span className="text-sm font-semibold truncate">{d.name_bg}</span>
                            </div>
                            <div className={`text-xs mt-0.5 ${mutedCls}`}>
                              {(d.current_level_label || "").replace(/cm/gi, "см")}
                            </div>
                          </div>

                          {/* Arrow + delta */}
                          <div className="flex flex-col items-center justify-center py-1">
                            <ArrowRight className={`w-5 h-5 ${d.already_optimal ? "text-slate-400" : "text-emerald-500"}`} />
                            {!d.already_optimal ? (
                              <div className="text-[11px] text-emerald-500 font-semibold mt-0.5">
                                −{Math.abs(d.delta_contribution).toFixed(2)}
                              </div>
                            ) : (
                              <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-0.5">
                                <Award className="w-3 h-3" /> Оптимално
                              </div>
                            )}
                          </div>

                          {/* Target */}
                          <div className="min-w-0 md:text-right">
                            <div className={`text-[10px] uppercase tracking-wide ${mutedCls}`}>Цел</div>
                            <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 truncate">
                              {d.already_optimal ? "Запази нивото" : (d.suggested_level_label || "").replace(/cm/gi, "см")}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* iter992 — Weekly plan (Sprint 4 lite): 7-day habit tracker
                derived from the top-5 BTRE priorities. */}
            <WeeklyPlanPanel />
          </>
        )}
      </div>
    </div>
  );
}
